<template>
  <Teleport to="body">
    <div class="toast-container">
      <TransitionGroup name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="toast"
          :class="`toast-${toast.type}`"
        >
          <div class="toast-content">
            <div class="toast-icon">
              <i :class="getIconClass(toast.type)"></i>
            </div>
            <div class="toast-text">
              <h4 class="toast-title">{{ toast.title }}</h4>
              <p v-if="toast.message" class="toast-message">{{ toast.message }}</p>
            </div>
            <button @click="removeToast(toast.id)" class="toast-close">
              <span>×</span>
            </button>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useToast } from '../../composables/useToast';

const { toasts, remove } = useToast();

const removeToast = (id: string) => {
  remove(id);
};

const getIconClass = (type: string) => {
  switch (type) {
    case 'success':
      return 'icon-success';
    case 'error':
      return 'icon-error';
    case 'warning':
      return 'icon-warning';
    case 'info':
      return 'icon-info';
    default:
      return 'icon-info';
  }
};
</script>

<style scoped>
@import '../../assets/main.css';

.toast-container {
  position: fixed;
  top: var(--spacing-4);
  right: var(--spacing-4);
  z-index: 9999;
  pointer-events: none;
}

.toast {
  background: white;
  border-radius: var(--radius-lg);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  margin-bottom: var(--spacing-3);
  min-width: 320px;
  max-width: 420px;
  pointer-events: all;
  border-left: 4px solid;
  animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-success {
  border-left-color: var(--color-primary-500);
}

.toast-error {
  border-left-color: #dc2626;
}

.toast-warning {
  border-left-color: #f59e0b;
}

.toast-info {
  border-left-color: #3b82f6;
}

.toast-content {
  display: flex;
  align-items: start;
  padding: var(--spacing-4);
  gap: var(--spacing-3);
}

.toast-icon {
  flex-shrink: 0;
  width: var(--spacing-6);
  height: var(--spacing-6);
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-success::before {
  content: '✓';
  color: var(--color-primary-500);
  font-size: var(--font-size-lg);
  font-weight: bold;
}

.icon-error::before {
  content: '✕';
  color: #dc2626;
  font-size: var(--font-size-lg);
  font-weight: bold;
}

.icon-warning::before {
  content: '!';
  color: #f59e0b;
  font-size: var(--font-size-lg);
  font-weight: bold;
}

.icon-info::before {
  content: 'i';
  color: #3b82f6;
  font-size: var(--font-size-lg);
  font-weight: bold;
}

.toast-text {
  flex: 1;
  min-width: 0;
}

.toast-title {
  margin: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-neutral-dark-gray);
  line-height: 1.5;
}

.toast-message {
  margin: var(--spacing-1) 0 0 0;
  font-size: var(--font-size-sm);
  color: rgb(107 114 128);
  line-height: 1.5;
}

.toast-close {
  flex-shrink: 0;
  background: none;
  border: none;
  color: rgb(156 163 175);
  font-size: var(--font-size-2xl);
  cursor: pointer;
  padding: 0;
  width: var(--spacing-6);
  height: var(--spacing-6);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  transition: all 0.2s ease;
  margin-left: var(--spacing-2);
}

.toast-close:hover {
  background: rgb(249 250 251);
  color: rgb(107 114 128);
}

/* Transition animations */
.toast-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 1, 1);
}

.toast-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.toast-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Mobile responsiveness */
@media (max-width: 640px) {
  .toast-container {
    left: var(--spacing-4);
    right: var(--spacing-4);
  }
  
  .toast {
    min-width: unset;
    width: 100%;
  }
}
</style>
